import React from "react";
import { Check, Shield, Clock, Users, Medal, HeartHandshake, DollarSign } from "lucide-react";

const POINTS = [
  { icon: Shield, title: "Veteran-Owned Values", text: "Discipline, accountability, and service to the community are built into every repair." },
  { icon: DollarSign, title: "Transparent Estimates", text: "Written, itemized quotes before work starts. No surprises, no fine print." },
  { icon: Check, title: "No Unnecessary Upsells", text: "We only recommend what your vehicle actually needs — period." },
  { icon: Clock, title: "Fast Turnaround", text: "Most common repairs completed same-day so you're not stranded." },
  { icon: Users, title: "Friendly, Knowledgeable Staff", text: "Straight answers in plain English. Our customers feel respected, not rushed." },
  { icon: Medal, title: "Fair, Honest Pricing", text: "Local, competitive rates backed by a nationwide parts & labor warranty." },
  { icon: HeartHandshake, title: "Trusted by Local Families", text: "Madison Heights drivers return because they know they're taken care of." },
];

export default function WhyChooseUs() {
  return (
    <section
      id="why-us"
      data-testid="why-choose-us-section"
      className="relative py-20 md:py-28 bg-[#F8F9FA]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left header column */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-[#D32F2F]" />
              <span className="font-mono-label text-[#D32F2F]">Why Choose Us</span>
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-[#0B132B] leading-[1.05]">
              Why Madison Heights Trusts{" "}
              <span className="text-[#D32F2F]">Veteran's Auto Repair Center.</span>
            </h2>
            <p className="mt-6 text-[#4A5568] text-lg leading-relaxed">
              Built on military values and backed by real certifications — we
              treat your vehicle (and your wallet) with the respect they
              deserve.
            </p>

            <div className="mt-8 grid grid-cols-2 gap-4 max-w-md">
              <div className="border border-[#E2E8F0] bg-white p-5">
                <div className="font-display text-3xl md:text-4xl font-bold text-[#0B132B]">
                  15+
                </div>
                <div className="mt-1 text-xs font-mono-label text-[#4A5568]">
                  Years Combined Exp.
                </div>
              </div>
              <div className="border border-[#E2E8F0] bg-white p-5">
                <div className="font-display text-3xl md:text-4xl font-bold text-[#0B132B]">
                  100%
                </div>
                <div className="mt-1 text-xs font-mono-label text-[#4A5568]">
                  Honest Estimates
                </div>
              </div>
              <div className="border border-[#E2E8F0] bg-white p-5">
                <div className="font-display text-3xl md:text-4xl font-bold text-[#D32F2F]">
                  5.0★
                </div>
                <div className="mt-1 text-xs font-mono-label text-[#4A5568]">
                  Local Review Avg.
                </div>
              </div>
              <div className="border border-[#E2E8F0] bg-white p-5">
                <div className="font-display text-3xl md:text-4xl font-bold text-[#0B132B]">
                  Same-
                  <br />
                  Day
                </div>
                <div className="mt-1 text-xs font-mono-label text-[#4A5568]">
                  Most Repairs
                </div>
              </div>
            </div>
          </div>

          {/* Right feature list */}
          <div className="lg:col-span-7">
            <ul className="divide-y divide-[#E2E8F0] border-y border-[#E2E8F0] bg-white">
              {POINTS.map(({ icon: Icon, title, text }, i) => (
                <li
                  key={title}
                  data-testid={`why-point-${i}`}
                  className="group flex gap-5 p-5 md:p-6 hover:bg-[#0B132B] transition-colors duration-300"
                >
                  <div className="shrink-0 w-12 h-12 flex items-center justify-center bg-[#F8F9FA] border border-[#E2E8F0] group-hover:bg-[#D32F2F] group-hover:border-[#D32F2F] transition-colors">
                    <Icon className="w-5 h-5 text-[#D32F2F] group-hover:text-white transition-colors" strokeWidth={2.5} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline gap-3">
                      <span className="font-mono-label text-[#4A5568] group-hover:text-[#D32F2F] transition-colors">
                        /{String(i + 1).padStart(2, "0")}
                      </span>
                      <h3 className="font-display text-lg md:text-xl font-semibold text-[#0B132B] group-hover:text-white transition-colors">
                        {title}
                      </h3>
                    </div>
                    <p className="mt-2 text-sm md:text-base text-[#4A5568] group-hover:text-white/80 transition-colors leading-relaxed">
                      {text}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
